import { useMemo } from 'react'

import { TProgressSections } from 'pages-config'

import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { usePageConfig } from 'hooks/usePageConfig'

import { PageId } from 'page-constants'

const NO_PROGRESS_SECTIONS: TProgressSections = {}

type TProgressSection = {
  titleKey: string
  progress: number
}

type TSectionedProgress = {
  hasSectionedProgress: boolean
  currentSectionTitleKey: string
  sections: TProgressSection[]
}

const groupPageIdsBySection = (
  sections: TProgressSections,
  pageIds: PageId[],
): PageId[][] => {
  const sectionStartPageIds = Object.values(sections)
  const pageIdsBySection: PageId[][] = sectionStartPageIds.map(() => [])
  let sectionIndex = -1

  pageIds.forEach((pageId) => {
    const startedSectionIndex = sectionStartPageIds.findIndex((startPageIds) =>
      startPageIds.includes(pageId),
    )

    if (startedSectionIndex >= 0) sectionIndex = startedSectionIndex
    if (sectionIndex >= 0) pageIdsBySection[sectionIndex].push(pageId)
  })

  return pageIdsBySection
}

export const useSectionedProgress = (): TSectionedProgress => {
  const { progressSections = NO_PROGRESS_SECTIONS } = usePageConfig()
  const { currentPageId, hasProgressbar, pagesWithProgressBar } =
    useGetPageInfo()

  return useMemo(() => {
    const sectionTitleKeys = Object.keys(progressSections)
    const pageIdsBySection = groupPageIdsBySection(
      progressSections,
      pagesWithProgressBar.map(({ id }) => id),
    )
    const currentSectionIndex = pageIdsBySection.findIndex((pageIds) =>
      pageIds.includes(currentPageId as PageId),
    )

    const getSectionProgress = (sectionIndex: number): number => {
      if (sectionIndex < currentSectionIndex) return 1
      if (sectionIndex > currentSectionIndex) return 0

      const sectionPageIds = pageIdsBySection[sectionIndex]
      const passedPagesCount =
        sectionPageIds.indexOf(currentPageId as PageId) + 1

      return passedPagesCount / sectionPageIds.length
    }

    return {
      hasSectionedProgress: !!hasProgressbar && currentSectionIndex >= 0,
      currentSectionTitleKey: sectionTitleKeys[currentSectionIndex] ?? '',
      sections: sectionTitleKeys.map((titleKey, sectionIndex) => ({
        titleKey,
        progress: getSectionProgress(sectionIndex),
      })),
    }
  }, [currentPageId, hasProgressbar, pagesWithProgressBar, progressSections])
}
